import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchStaffById, getRole } from '../../../api/staff';
import {
  Button,
  Form,
  FormTitle,
  Input,
  Modal,
  Radio,
  Select,
  SubContent,
  useAsync,
  useBreadcrumbRoutes,
  Wrapper,
} from '@maxtropy/components';

import styles from './index.module.scss';
import { Col, Row, Space } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { LeftNavPreference, OuStaffType } from '../../../api/const';
import { getCpOrgInfoList, getCpUserInfo, updateCustomerAuthority } from '../../../api/customer';
import OuList, { ChildRef } from './components/OuList';
import { useRequest } from 'ahooks';

const { useForm } = Form;

const Detail = ({ isEdit }: { isEdit?: boolean }) => {
  const { id } = useParams<{ id: string }>();

  const [form] = useForm();
  const [selectedMcid, setSelectedMcid] = useState<string>();
  const [staffId, setStaffId] = useState<number>();
  const [loading, setLoading] = useState(false);

  const ouListRef = useRef<ChildRef>(null);

  const navigate = useNavigate();
  const breadcrumbRoutes = useBreadcrumbRoutes();

  const title = isEdit ? '编辑员工' : '查看员工';

  const routes = [{ name: title }];

  // 获取外部组织
  const { data: cpOrgList } = useRequest(getCpOrgInfoList);

  const getRoleRequest = useCallback(async () => {
    if (selectedMcid) {
      return await getRole(selectedMcid);
    }
    return undefined;
  }, [selectedMcid]);

  // 获取角色
  const roles = useAsync(getRoleRequest);

  useEffect(() => {
    if (!id) return;
    // 获取用户信息
    getCpUserInfo(id).then(res => {
      form.setFieldsValue(res);
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!id) return;
    // 获取员工信息
    fetchStaffById(id).then(res => {
      setSelectedMcid(res.mcid);
      setStaffId(res.id);
      form.setFieldsValue({
        ...res,
      });
    });
  }, [form, id]);

  const onClose = () => {
    if (isEdit) {
      Modal.confirm({
        content: '确认放弃未保存的修改返回列表吗?',
        onOk: () => {
          navigate(`/system/customer/staff`);
        },
      });
    } else {
      navigate(`/system/customer/staff`);
    }
  };

  const handleClick = () => {
    form.validateFields().then(values => {
      setLoading(true);
      const ouData = ouListRef.current?.getValue() ?? [];
      console.log('子组件传回的值:', values, ouData);
      const memberList = ouData.filter(item => item.memberType === OuStaffType.MEMBER)?.map(i => i.id);
      const adminList = ouData.filter(item => item.memberType === OuStaffType.ADMINISTRATOR)?.map(i => i.id);
      updateCustomerAuthority({
        ...values,
        ouList: [
          { ouMemberType: OuStaffType.MEMBER, ouIds: memberList },
          { ouMemberType: OuStaffType.ADMINISTRATOR, ouIds: adminList },
        ],
      })
        .then(() => {
          navigate(`/system/customer/staff`);
        })
        .finally(() => setLoading(false));
    });
  };

  return (
    <Wrapper className={styles.wrapperPadding} routes={[...(breadcrumbRoutes?.routes ?? []), ...routes]}>
      <FormTitle title={title} />
      <Form form={form} layout="vertical" validateTrigger={['onBlur']}>
        <SubContent title="外部员工信息">
          <Row>
            <Col span={8}>
              <Form.Item label="手机号码" name="cellphone" required>
                <Input disabled={true} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="用户姓名" name="name" required>
                <Input disabled={true} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="用户账号" name="username" required>
                <Input disabled={true} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="navigationPreference" label="左侧导航栏" rules={[{ required: true, message: '请选择' }]}>
                <Radio.Group disabled={!isEdit}>
                  <Radio value={LeftNavPreference.OPEN_CLOSE}>收起/展开</Radio>
                  <Radio value={LeftNavPreference.ALL_OPEN}>一直展开</Radio>
                  <Radio value={LeftNavPreference.ALL_CLOSE}>直接隐藏</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="mcids" label="所在组织" rules={[{ required: true, message: '请选择组织' }]}>
                <Select
                  style={{ width: '100%' }}
                  placeholder="请选择"
                  showSearch
                  disabled={!isEdit}
                  onChange={value => {
                    setSelectedMcid(value);
                    form.setFieldsValue({
                      roleId: undefined,
                    });
                  }}
                  options={(cpOrgList?.list ?? []).map(item => ({ label: item.orgName, value: item.orgId }))}
                />
              </Form.Item>
            </Col>
          </Row>
        </SubContent>
        <SubContent title="员工权限">
          <Row>
            <Col span={8}>
              <Form.Item
                label="角色"
                required
                help={
                  <div className={styles.tips}>
                    <InfoCircleOutlined />
                    不分配角色则用户登录并选择此工号后无法访问除用户中心之外的页面
                  </div>
                }
              >
                <Form.Item name="roleId" noStyle rules={[{ required: true, message: '请选择角色' }]}>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="请选择角色"
                    showSearch
                    filterOption={(input, option) => option!.label.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                    disabled={!isEdit}
                    options={(roles?.list ?? []).map(item => ({ label: item.name, value: item.id }))}
                  />
                </Form.Item>
              </Form.Item>
            </Col>
          </Row>
          <OuList staffId={staffId} ref={ouListRef} isEdit={isEdit} />
          <Space style={{ marginTop: 24 }}>
            <Button type="primary" onClick={handleClick} loading={loading}>
              保存
            </Button>
            <Button onClick={onClose}>取消</Button>
          </Space>
        </SubContent>
      </Form>
    </Wrapper>
  );
};

export default Detail;
